<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import axios from 'axios';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    visible: Boolean,
    loading: {
        type: Boolean,
        default: false,
    },
    clientLocale: {
        type: String,
        default: 'en',
    },
});

const emit = defineEmits(['update:visible', 'confirm']);

const LOCALES = ['en', 'pt', 'es'];
const localeOptions = LOCALES.map(l => ({ label: l.toUpperCase(), value: l }));

const locale = ref(LOCALES.includes(props.clientLocale) ? props.clientLocale : 'en');
const message = ref('');
const defaults = ref({ en: '', pt: '', es: '' });
const loaded = ref(false);
const loadingDefaults = ref(false);

// Mirrors the server-side fallback (locale -> English), so the preview always
// matches the message that would actually be sent
const defaultFor = (l) => defaults.value[l] || defaults.value.en || '';
const currentDefault = computed(() => defaultFor(locale.value));

const loadDefaults = async () => {
    if (loaded.value || loadingDefaults.value) return;
    loadingDefaults.value = true;

    try {
        const url = proxy.route
            ? proxy.route('admin.email-settings.default-messages', 'content_client_approval_request')
            : '/admin/email-settings/default-messages/content_client_approval_request';
        const { data } = await axios.get(url);
        defaults.value = { ...defaults.value, ...(data.default_messages?.client || {}) };
        loaded.value = true;

        if (!message.value.trim()) {
            message.value = currentDefault.value;
        }
    } catch (e) {
        // Modal still works as a free-text message; retried on next open
    } finally {
        loadingDefaults.value = false;
    }
};

watch(() => props.visible, (visible) => {
    if (visible) {
        locale.value = LOCALES.includes(props.clientLocale) ? props.clientLocale : 'en';
        loadDefaults();
        if (!message.value.trim()) {
            message.value = currentDefault.value;
        }
    }
});

// Switching language swaps an untouched text to the new language's default
watch(locale, (newLocale, oldLocale) => {
    if (message.value.trim() === defaultFor(oldLocale).trim()) {
        message.value = defaultFor(newLocale);
    }
});

const restoreDefault = () => {
    message.value = currentDefault.value;
};

const handleConfirm = () => {
    const text = message.value.trim();
    emit('confirm', text && text !== currentDefault.value.trim() ? text : null, locale.value);
    message.value = '';
};

const handleClose = () => {
    message.value = '';
    emit('update:visible', false);
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="(v) => { if (!v) handleClose(); }"
        modal
        :header="$t('Send for Client Approval')"
        :style="{ width: '500px' }"
        :breakpoints="{ '640px': '95vw' }"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <i class="pi pi-send text-blue-500 text-xl mt-0.5"></i>
                <p class="text-surface-700 text-sm">
                    {{ $t('Are you sure you want to send this Google Docs link to the client for approval?') }}
                </p>
            </div>
            <div>
                <div class="flex items-center justify-between mb-2">
                    <label class="text-sm font-medium text-surface-700">
                        {{ $t('Message to client (Optional)') }}
                    </label>
                    <div class="flex items-center gap-2">
                        <SelectButton
                            v-model="locale"
                            :options="localeOptions"
                            optionLabel="label"
                            optionValue="value"
                            :allowEmpty="false"
                            :disabled="loading"
                        />
                        <Button
                            icon="pi pi-refresh"
                            text
                            size="small"
                            severity="secondary"
                            v-tooltip.top="$t('Restore default')"
                            @click="restoreDefault"
                        />
                    </div>
                </div>
                <Textarea
                    v-model="message"
                    rows="4"
                    autoResize
                    class="w-full"
                    maxlength="5000"
                    :placeholder="$t('Add a note or instructions for the client...')"
                    :disabled="loading"
                />
                <p class="text-xs text-surface-500 mt-2">
                    {{ $t('Leave empty (or unchanged) to use the default message configured in Email Settings.') }}
                </p>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-3">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    :disabled="loading"
                    @click="handleClose"
                />
                <Button
                    :label="$t('Send')"
                    icon="pi pi-send"
                    severity="info"
                    :loading="loading"
                    @click="handleConfirm"
                />
            </div>
        </template>
    </Dialog>
</template>
